import { lazy } from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';

// Lazy load proposal pages
const ProposalsList = lazy(() => import('./ProposalsList'));
const NewProposal = lazy(() => import('./NewProposal'));
const ProposalDetail = lazy(() => import('./ProposalDetail'));
const EditProposal = lazy(() => import('./EditProposal'));

const proposalsModuleConfig: ModuleConfig = {
  id: 'proposals',
  name: 'Proposals',
  description: 'Manage business proposals and signature tracking',
  version: '1.0.0',
  enabled: true,
  category: 'core',
  order: 3,
  icon: '📄',
  color: 'blue',
  routes: [
    {
      path: '/proposals',
      component: ProposalsList,
      permissionLevel: 'view',
      exact: true,
      useLayout: true,
    },
    {
      path: '/proposals/new',
      component: NewProposal,
      permissionLevel: 'create',
      exact: true,
      useLayout: true,
    },
    {
      path: '/proposals/:id/edit',
      component: EditProposal,
      permissionLevel: 'edit',
      exact: true,
      useLayout: true,
    },
    {
      path: '/proposals/:id',
      component: ProposalDetail,
      permissionLevel: 'view',
      exact: true,
      useLayout: true,
    },
  ],
  navigation: [
    {
      label: 'All Proposals',
      path: '/proposals',
      icon: '📋',
      permissionLevel: 'view',
    },
    {
      label: 'New Proposal',
      path: '/proposals/new',
      icon: '➕',
      permissionLevel: 'create',
    },
  ],
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },
  quickActions: [
    {
      label: 'Create Proposal',
      path: '/proposals/new',
      icon: '➕',
      description: 'Create a new business proposal',
    },
  ],

  // 📝 CHANGELOG
  changelog: [
    {
      version: "1.0.0",
      releaseDate: "2025-11-22",
      title: "Stable Release - Production Ready",
      features: [
        "✅ Production-ready proposal management system",
        "✅ Digital signature capture and tracking",
        "✅ PDF generation for signed agreements",
        "✅ Custom proposal templates",
        "✅ Role-based access control",
        "✅ Complete audit trail for signatures"
      ],
      notes: "Official stable release with comprehensive proposal and signature management."
    },
    {
      version: "0.4.0",
      releaseDate: "2024-10-07",
      title: "Task Visibility & Code Quality",
      features: [
        "✨ Enhanced project tasks visibility (62 lines)",
        "✨ Improved ProposalSigningSection UX (47 insertions)",
        "🔨 Applied refactoring best practices (60 lines)",
        "✨ Better code organization and maintainability"
      ],
      notes: "Code quality improvements and enhanced task presentation."
    },
    {
      version: "0.3.0",
      releaseDate: "2024-10-05",
      title: "Visual Effects & User Experience",
      features: [
        "✨ Enhanced One Group proposal page with visual effects (916 insertions)",
        "✨ Improved EditProposal component (150 lines)",
        "✨ Enhanced NewProposal workflow (116 lines)",
        "✨ Better ProposalsList UI (47 lines)",
        "✨ Advanced ProposalSigningSection (777 lines)"
      ],
      notes: "Major UX improvements with visual enhancements and animations."
    },
    {
      version: "0.2.0",
      releaseDate: "2024-10-04",
      title: "Complete Module Structure",
      features: [
        "✅ Created EditProposal component (309 lines)",
        "✅ Created NewProposal component (271 lines)",
        "✅ Created ProposalDetail component (224 lines)",
        "✅ Created ProposalsList component (276 lines)",
        "✅ Implemented ProposalSigningSection (763 lines)",
        "✅ Created SignatureCapture component (125 lines)",
        "✅ Created SignedAgreementPDF component (361 lines)",
        "✅ Added comprehensive usage documentation",
        "✅ Configured module routing and permissions"
      ],
      notes: "Complete proposal module implementation with 2,544 lines of business logic."
    },
    {
      version: "0.1.0",
      releaseDate: "2024-10-04",
      title: "Initial Proposal Draft",
      features: [
        "✅ Created One Group proposal template (794 lines)",
        "✅ Initial proposal structure and layout",
        "✅ Basic proposal rendering",
        "✅ Foundation for signature workflow"
      ],
      notes: "Initial proposal system with template-based approach."
    }
  ],

  // 🗺️ ROADMAP
  roadmap: []

};

export default proposalsModuleConfig;
